import React, { FormEvent, useState } from 'react';

import apiClient, { API_URL } from '../../services/api';
import { SocialAccount } from '../../types/api';

export type ConnectablePlatform = 'bluesky' | 'instagram' | 'facebook' | 'tiktok' | 'reddit';

interface ConnectAccountModalProps {
  platform: ConnectablePlatform;
  /** Whether the platform's OAuth credentials are configured on the
   *  server. Bluesky doesn't need any so this is always true for it. */
  configured: boolean;
  onClose: () => void;
  onConnected: (account: SocialAccount) => void;
}

const PLATFORM_LABEL: Record<ConnectablePlatform, string> = {
  bluesky: 'Bluesky',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  reddit: 'Reddit',
};

/**
 * Per-platform connect flow shown in a modal. Each platform owns its
 * own UX:
 *
 *  - Bluesky: one-page form with handle + app password, hits the
 *    server's `/connect/bluesky/` directly. No redirect.
 *  - Instagram: walkthrough of prerequisites (Business/Creator
 *    account type, tester invite accepted) BEFORE the OAuth
 *    redirect, because skipping any of those silently breaks the
 *    callback later.
 *  - Facebook: similar Page-prerequisite walkthrough then redirect.
 *  - TikTok: lightweight redirect with a single info screen.
 *
 * OAuth platforms full-page-redirect to the backend; the user comes
 * back to /accounts?connected=<platform> which the page surfaces as a
 * flash. This component closes itself once the redirect kicks off.
 */
export default function ConnectAccountModal({
  platform, configured, onClose, onConnected,
}: ConnectAccountModalProps) {
  if (!configured) {
    return <NotConfiguredModal platform={platform} onClose={onClose} />;
  }
  if (platform === 'bluesky') {
    return <BlueskyModal onClose={onClose} onConnected={onConnected} />;
  }
  if (platform === 'instagram') {
    return <InstagramModal onClose={onClose} />;
  }
  if (platform === 'facebook') {
    return <FacebookModal onClose={onClose} />;
  }
  if (platform === 'reddit') {
    return <RedditModal onClose={onClose} />;
  }
  return <TikTokModal onClose={onClose} />;
}

// ---------------------------------------------------------------------- //
// Shared modal shell — head/body/foot layout used by every variant.
// ---------------------------------------------------------------------- //
function ModalShell({
  title, onClose, children, footer,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <div
      className="modal modal--centered"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal__panel modal__panel--narrow">
        <header className="modal__head">
          <h3>{title}</h3>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={onClose}
          >×</button>
        </header>
        <div className="modal__body">{children}</div>
        <footer className="modal__foot">{footer}</footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------- //
// "Not configured" — the platform isn't wired on the backend.
// ---------------------------------------------------------------------- //
function NotConfiguredModal({
  platform, onClose,
}: { platform: ConnectablePlatform; onClose: () => void }) {
  const KEY = platform.toUpperCase();
  return (
    <ModalShell
      title={`Connect ${PLATFORM_LABEL[platform]}`}
      onClose={onClose}
      footer={<button type="button" className="primary" onClick={onClose}>OK</button>}
    >
      <p>
        {PLATFORM_LABEL[platform]} OAuth isn't configured on this server.
        Set <code>{KEY}_CLIENT_ID</code>, <code>{KEY}_CLIENT_SECRET</code>,
        and <code>{KEY}_REDIRECT_URI</code> in <code>backend/.env</code> to
        enable.
      </p>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------- //
// Bluesky — handle + app password form, no redirect.
// ---------------------------------------------------------------------- //
function BlueskyModal({
  onClose, onConnected,
}: { onClose: () => void; onConnected: (a: SocialAccount) => void }) {
  const [handle, setHandle] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const account = await apiClient.post<SocialAccount>(
        '/accounts/social-accounts/connect/bluesky/',
        { handle: handle.trim(), app_password: appPassword },
      );
      onConnected(account);
    } catch (err) {
      setError((err as Error).message || 'Failed to connect account');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title="Connect Bluesky"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            form="bluesky-connect-form"
            className="primary"
            disabled={submitting || !handle.trim() || !appPassword}
          >
            {submitting ? 'Connecting…' : 'Connect Bluesky'}
          </button>
        </>
      }
    >
      <form id="bluesky-connect-form" className="account-wizard-form" onSubmit={submit}>
        <label>
          Handle
          <input
            type="text"
            placeholder="you.bsky.social"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          App password
          <input
            type="password"
            placeholder="xxxx-xxxx-xxxx-xxxx"
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <small>
            Create one at{' '}
            <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noreferrer">
              bsky.app/settings/app-passwords
            </a>{' '}
            — not your main password.
          </small>
        </label>
        {error && <div className="banner banner--error">{error}</div>}
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------- //
// Multi-step walkthrough used by IG/FB — checklist of prerequisites
// the user has to satisfy on the OTHER platform before the OAuth
// redirect can land successfully.
// ---------------------------------------------------------------------- //
interface WalkthroughStep {
  title: string;
  body: React.ReactNode;
}

function WalkthroughModal({
  platform, steps, onClose,
}: {
  platform: ConnectablePlatform;
  steps: WalkthroughStep[];
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(0);
  const last = idx === steps.length - 1;
  const step = steps[idx];

  function next(): void {
    if (last) {
      // Full-page redirect through the backend OAuth authorize endpoint.
      window.location.assign(`${API_URL}/accounts/oauth/${platform}/authorize/`);
    } else {
      setIdx((n) => n + 1);
    }
  }

  return (
    <ModalShell
      title={`Connect ${PLATFORM_LABEL[platform]}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="ghost"
            onClick={() => (idx === 0 ? onClose() : setIdx((n) => n - 1))}
          >
            {idx === 0 ? 'Cancel' : 'Back'}
          </button>
          <button type="button" className="primary" onClick={next}>
            {last ? `Authorize ${PLATFORM_LABEL[platform]}` : `Next (${idx + 2} of ${steps.length})`}
          </button>
        </>
      }
    >
      <div className="connect-walkthrough-progress muted">
        Step {idx + 1} of {steps.length}
      </div>
      <h4 className="connect-walkthrough-title">{step.title}</h4>
      <div>{step.body}</div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------- //
// Instagram — prerequisites then OAuth redirect.
// ---------------------------------------------------------------------- //
function InstagramModal({ onClose }: { onClose: () => void }) {
  const steps: WalkthroughStep[] = [
    {
      title: 'Use a Business or Creator account',
      body: (
        <>
          <p>
            Instagram's API doesn't accept posts from Personal accounts.
            Switch the account you want to connect to <strong>Business</strong>
            {' '}or <strong>Creator</strong> if it isn't already:
          </p>
          <ol>
            <li>Open the Instagram app and go to your profile.</li>
            <li>Menu → Settings and privacy → Account type and tools.</li>
            <li>Switch to professional account → pick Business or Creator.</li>
          </ol>
          <p className="muted">It's free. You can switch back any time.</p>
        </>
      ),
    },
    {
      title: 'Accept the tester invite',
      body: (
        <>
          <p>
            While the Megaphony Meta app is in Development mode, only
            invited testers can connect. If you haven't been invited yet,
            ask the app owner to add your IG username under{' '}
            <em>App Roles → Instagram Testers</em> at developers.facebook.com.
          </p>
          <p>Then accept the invite inside Instagram:</p>
          <ol>
            <li>Instagram app → Settings and privacy → Apps and websites.</li>
            <li>Tester Invites → accept the Megaphony invite.</li>
          </ol>
        </>
      ),
    },
    {
      title: 'Authorize Megaphony',
      body: (
        <>
          <p>
            You'll be redirected to Instagram to authorize Megaphony to
            post on your behalf, then sent back here. Make sure you're
            logged into the right Instagram account in your browser
            first.
          </p>
          <p className="muted">
            Megaphony only requests the scopes needed to read your
            profile and publish posts — nothing else.
          </p>
        </>
      ),
    },
  ];
  return <WalkthroughModal platform="instagram" steps={steps} onClose={onClose} />;
}

// ---------------------------------------------------------------------- //
// Facebook — Page-prerequisite walkthrough then OAuth redirect.
// ---------------------------------------------------------------------- //
function FacebookModal({ onClose }: { onClose: () => void }) {
  const steps: WalkthroughStep[] = [
    {
      title: 'You need a Facebook Page',
      body: (
        <>
          <p>
            Megaphony posts to Facebook <strong>Pages</strong>, not to your
            personal profile. If you don't have a Page yet, create one at{' '}
            <a href="https://www.facebook.com/pages/create" target="_blank" rel="noreferrer">
              facebook.com/pages/create
            </a>.
          </p>
          <p className="muted">
            You'll be the admin of the Page, which is what lets the
            access token work.
          </p>
        </>
      ),
    },
    {
      title: 'Authorize Megaphony',
      body: (
        <>
          <p>
            You'll be redirected to Facebook. On the consent screen,
            <strong> select every Page you want to post to</strong> —
            Megaphony creates one row per Page you grant access to. You
            can add/remove pages later by reconnecting Facebook.
          </p>
        </>
      ),
    },
  ];
  return <WalkthroughModal platform="facebook" steps={steps} onClose={onClose} />;
}

// ---------------------------------------------------------------------- //
// Reddit — one info screen then OAuth redirect. Text-only for v1.
// ---------------------------------------------------------------------- //
function RedditModal({ onClose }: { onClose: () => void }) {
  const steps: WalkthroughStep[] = [
    {
      title: 'Where Reddit posts land',
      body: (
        <>
          <p>
            After authorizing, posts go to your <strong>profile feed</strong>
            {' '}(u/<em>your-username</em>) by default. You can change the
            target subreddit per account later — useful when you have a
            specific community to post to.
          </p>
          <p className="muted">
            For v1, Megaphony submits text posts only — the first line of
            your post description becomes the Reddit title, the rest
            becomes the body. Image / video upload to Reddit is on the
            roadmap.
          </p>
        </>
      ),
    },
    {
      title: 'Authorize Megaphony',
      body: (
        <>
          <p>
            You'll be redirected to Reddit to grant Megaphony permission
            to read your username and submit posts on your behalf. Make
            sure you're logged into the right Reddit account in your
            browser first.
          </p>
        </>
      ),
    },
  ];
  return <WalkthroughModal platform="reddit" steps={steps} onClose={onClose} />;
}

// ---------------------------------------------------------------------- //
// TikTok — one info screen then OAuth redirect. Video-only.
// ---------------------------------------------------------------------- //
function TikTokModal({ onClose }: { onClose: () => void }) {
  const steps: WalkthroughStep[] = [
    {
      title: 'Authorize Megaphony to post videos',
      body: (
        <>
          <p>
            You'll be redirected to TikTok to grant Megaphony permission
            to upload videos to your account. TikTok only accepts video
            uploads — image posts go to Instagram / Facebook instead.
          </p>
          <p className="muted">
            Make sure you're logged into the right TikTok account in your
            browser first.
          </p>
        </>
      ),
    },
  ];
  return <WalkthroughModal platform="tiktok" steps={steps} onClose={onClose} />;
}
